import React, { createContext, useContext, useEffect, useState } from 'react';
import localforage from 'localforage';

const AuthContext = createContext();

const parseStudent = (value) => {
  if (!value || typeof value === 'object') return value || null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [studentId, setStudentId] = useState(null);
  const [student, setStudent] = useState(null);
  const [studentToken, setStudentToken] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // IndexedDB is asynchronous. Do not allow route guards to redirect until the
  // durable PWA session has finished loading.
  useEffect(() => {
    let mounted = true;

    const restoreSession = async () => {
      try {
        let [savedStudentId, savedStudent, savedToken] = await Promise.all([
          localforage.getItem('studentId'),
          localforage.getItem('studentData'),
          localforage.getItem('studentToken'),
        ]);

        // One-time migration keeps users signed in after this storage upgrade.
        if (!savedStudentId && !savedStudent && !savedToken) {
          const legacyId = localStorage.getItem('studentId');
          const legacyStudent = localStorage.getItem('studentData') || localStorage.getItem('student');
          const legacyToken = localStorage.getItem('studentToken');

          if (legacyId || legacyStudent || legacyToken) {
            savedStudentId = legacyId;
            savedStudent = parseStudent(legacyStudent);
            savedToken = legacyToken;
            await Promise.all([
              legacyId ? localforage.setItem('studentId', legacyId) : Promise.resolve(),
              legacyStudent ? localforage.setItem('studentData', savedStudent) : Promise.resolve(),
              legacyToken ? localforage.setItem('studentToken', legacyToken) : Promise.resolve(),
            ]);
          }
        }

        if (mounted) {
          setStudentId(savedStudentId || null);
          setStudent(parseStudent(savedStudent));
          setStudentToken(savedToken || null);
        }
      } catch (error) {
        console.warn('Unable to restore the saved session.', error);
      } finally {
        if (mounted) setIsInitializing(false);
      }
    };

    restoreSession();
    return () => { mounted = false; };
  }, []);

  // Persist profile changes made elsewhere in the app to IndexedDB.
  useEffect(() => {
    if (isInitializing) return;
    if (student) localforage.setItem('studentData', student);
    else localforage.removeItem('studentData');
  }, [student, isInitializing]);

  useEffect(() => {
    if (isInitializing) return;
    if (studentId) localforage.setItem('studentId', studentId);
    else localforage.removeItem('studentId');
  }, [studentId, isInitializing]);

  useEffect(() => {
    if (isInitializing) return;
    if (studentToken) localforage.setItem('studentToken', studentToken);
    else localforage.removeItem('studentToken');
  }, [studentToken, isInitializing]);

  const login = async ({ id, studentData, token = null }) => {
    await Promise.all([
      id ? localforage.setItem('studentId', id) : localforage.removeItem('studentId'),
      studentData ? localforage.setItem('studentData', studentData) : localforage.removeItem('studentData'),
      token ? localforage.setItem('studentToken', token) : localforage.removeItem('studentToken'),
    ]);

    setStudentId(id || null);
    setStudent(studentData || null);
    setStudentToken(token || null);
  };

  const logout = async () => {
    setStudentId(null);
    setStudent(null);
    setStudentToken(null);
    await Promise.all([
      localforage.removeItem('studentId'),
      localforage.removeItem('studentToken'),
      localforage.removeItem('studentData'),
    ]);
  };

  return (
    <AuthContext.Provider value={{
      studentId,
      setStudentId,
      student,
      setStudent,
      studentToken,
      setStudentToken,
      login,
      logout,
      isInitializing,
      isHydrated: !isInitializing,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
